import { BusinessError } from '@marketing-milk/interfaces'

export type Provider = 'meta' | 'google'
export type Severity = 'critical' | 'warning'

export interface BusinessErrorInfo {
  provider: Provider
  severity: Severity
  title: string
  summary: string
  steps: string[]
  learnMoreUrl?: string
}

// critical = actively blocks ad builder from working (matches the same split used in the FB Builder
// app's error banner); warning = a real problem but doesn't block campaign delivery. Shared by
// ErrorCenter and the Facebook Builder panel's per-field health display so both classify errors the
// same way. Steps differ by who connects the business (DRI-253): our system users, or one person's own
// Facebook login, the fallback when no system user of ours can reach both the Ad Account and Page. The
// two need genuinely different remediation instructions, so this is a function of both.
export const getBusinessErrorInfo = (
  error: BusinessError,
  onPerson: boolean
): BusinessErrorInfo => {
  const onSystemUsers = !onPerson
  // Builder's fix is always "click Reconnect on the field in the Facebook Builder panel" — the general
  // system-user/Reconnect explanation lives once in ErrorCenter's header (shown on system users),
  // not repeated per card here — just the concrete action for this specific field/permission.
  const builderReconnectSteps = (assetLabel: string, taskLabel: string) => [
    `In the Facebook Builder under Settings -> Social Media, click Reconnect on the ${assetLabel} field to have the system user re-request ${taskLabel}.`,
  ]

  // If the client shared the asset into our partner Business Manager (rather than adding a person
  // directly), what matters is the permission granted TO that Business Manager, not any individual's
  // personal access — this is easy to mix up, so spell out exactly where to go check it.
  const builderSharedAssetSteps = (assetLabel: string, taskLabel: string) => [
    `If the client shared this ${assetLabel} with our Business Manager instead of adding you personally, Reconnect depends on what permission OUR Business Manager was granted — not your own personal access.`,
    `To check: in OUR Business Manager, go to Users -> Partners.`,
    `Search for the client's Business Manager.`,
    `Open Assets assigned to you, and find the ${assetLabel}.`,
    `View its assignment and confirm it includes ${taskLabel} — that's what's actually needed here.`,
    `The client may need to confirm or re-grant that on their end.`,
  ]

  // On a person, everything runs on their own Facebook login: their access to the asset, and the login
  // itself still being valid.
  const personLoginSteps = (assetLabel: string, taskLabel: string) => [
    `The Facebook Builder panel under Settings -> Social Media shows who connects this business ("Connected by"). Check that they still have ${taskLabel} access on the ${assetLabel} in Facebook.`,
    'If the panel says their Facebook login expired, have them log in to Marketing Milk with Facebook again to refresh it.',
    "Or connect it another way: once the asset is shared into one of our Facebook Business Managers, click Use a system user on the field. If you have the access yourself, you can use your own Facebook login from the field's Fix dialog.",
  ]

  switch (error) {
    case BusinessError.ad_account_access_error:
      return {
        provider: 'meta',
        severity: 'critical',
        title: 'Ad Account Access Error',
        summary: onSystemUsers
          ? 'Facebook rejected our requests for this ad account. The system user is missing required permissions, or there is an issue with the Ad Account.'
          : 'Facebook rejected our requests for this ad account. The person connecting this business is missing permissions, their Facebook login has expired, or there is an issue with the Ad Account.',
        steps: onSystemUsers
          ? [
              ...builderReconnectSteps(
                'Ad Account',
                'Manage campaigns (ads) and View performance access'
              ),
              ...builderSharedAssetSteps(
                'Ad Account',
                'Manage campaigns (ads) and View performance'
              ),
              "If Reconnect still fails, check the ad account itself on Facebook's account settings screen for account-level restrictions.",
            ]
          : [
              ...personLoginSteps('Ad Account', 'Manage campaigns (ads) and View performance'),
              "If the issue persists, check the ad account itself on Facebook's account settings screen for account-level restrictions.",
              'Once resolved, you should be able to clear the error.',
            ],
      }
    case BusinessError.page_access_error:
      return {
        provider: 'meta',
        severity: 'critical',
        title: 'Page Access Error',
        summary: onSystemUsers
          ? 'The system user is missing required permissions on this Page.'
          : 'The person connecting this business is missing permissions on this Page, or their Facebook login has expired.',
        steps: onSystemUsers
          ? [
              ...builderReconnectSteps('Page', 'Ads and Insights access'),
              ...builderSharedAssetSteps('Page', 'Ads and Insights'),
            ]
          : [
              ...personLoginSteps('Page', 'Ads and Insights'),
              'Once resolved, you should be able to clear the error.',
            ],
      }
    case BusinessError.lead_access_error:
      return {
        provider: 'meta',
        severity: 'warning',
        title: 'Lead Access Error',
        summary: onSystemUsers
          ? 'The system user is missing Lead access on this Page.'
          : 'The person connecting this business is missing Lead access on this Page, or their Facebook login has expired.',
        steps: onSystemUsers
          ? [
              'This usually happens one of two ways: custom Leads access on the Page was removed or reset in Facebook Business Settings after the system user was originally granted it, or the Page itself lost its relationship to whichever Business Manager the system user operates under.',
              ...builderReconnectSteps('Page', 'Lead access'),
              ...builderSharedAssetSteps('Page', 'Leads'),
            ]
          : [
              'This usually happens when custom Leads access on the Page was removed or reset in Facebook Business Settings.',
              ...personLoginSteps('Page', 'Leads'),
              'Once resolved, you should be able to clear the error.',
            ],
      }
    case BusinessError.offline_upload_error:
      return {
        provider: 'meta',
        severity: 'warning',
        title: 'Offline Upload Error',
        summary: 'Uploads to Facebook have failed due to improper configuration.',
        steps: onSystemUsers
          ? [
              'Make sure this business has a Dataset ID configured with the Conversions API, not an Offline Event Set ID.',
              ...builderReconnectSteps('Dataset', 'access to it'),
              'If you recently switched Ad Accounts, the Dataset may no longer be connected to the currently selected Ad Account — check the connection in Events Manager, or reselect the Dataset in the Facebook Builder panel above.',
            ]
          : [
              'Make sure this business has a Dataset ID configured with the Conversions API.',
              'If it is still using an Offline Event Set ID, uploads to Facebook will continue to fail — migrate it to a Dataset ID.',
            ],
      }
    // DRI-442. Only a business on system users can have it (it compares system user ids, and never fires on a
    // person, DRI-253), so there's no separate person copy.
    case BusinessError.fb_system_user_mismatch:
      return {
        provider: 'meta',
        severity: 'warning',
        // DRI-462: named for what it blocks; the setup behind it (two system users) is the explanation, not the name
        title: 'Ads Publish Warning',
        summary:
          "Publishing from the Ad Builder is blocked for this business. Ads are created by the Ad Account's system user, and Facebook requires that same system user to have Advertise access on the Page. This business's Page is on a different system user, and Facebook confirms the Ad Account's system user has no Advertise access on it, so Facebook would reject the ad sets and ads with \"Insufficient Page Permission to Run Ads\". Marketing Milk blocks the publish before anything is created. The fix is to use one system user for both the Ad Account and the Page. Reporting, leads and insights are not affected.",
        steps: [
          'In the Facebook Builder under Settings -> Social Media, click Fix on the Ad Account or Page field, then "Use for both", then Save.',
          "If no system user can reach both, the Ad Account and Page first need to be in the same Facebook Business Manager: share the Ad Account into the Facebook Business Manager that has the Page (or the Page into the one that has the Ad Account). In the Facebook Business Manager that owns the asset: Business Settings -> Accounts -> Ad Accounts (or Pages) -> select it -> Assign partners, and enter the other Facebook Business Manager's ID. The Fix dialog names both and their IDs.",
          'Another admin may need to approve: sharing creates a request that a different admin of the sending (owning) Facebook Business Manager approves under Requests -> Needs review. Each ad account is its own request.',
          "If the client's own Facebook Business Manager owns the Ad Account or Page, the client has to share it; it can't be done from our side.",
          'Once the share is approved, come back, click Fix, then "Use for both", and Save. This warning clears on the next check.',
        ],
      }
    case BusinessError.google_ads_access_error:
      return {
        provider: 'google',
        severity: 'warning',
        title: 'Google Ads Access Error',
        summary: 'Google rejected our requests for this Google Ads account.',
        steps: [
          "The authorizer's Google account is no longer connected.",
          'Have the authorizer reconnect their Google account under User Preferences.',
          'Or, update the Google Authorizer on Marketing Milk to a user who has a connected Google account.',
          'Once resolved, you should be able to clear the error.',
        ],
      }
  }
}

export const severityColors: Record<
  Severity,
  { border: string; background: string; text: string }
> = {
  critical: { border: '#dc3545', background: '#fdf2f3', text: '#7a1620' },
  warning: { border: '#ffc107', background: '#fffaf0', text: '#7a5b00' },
}
